import React, { forwardRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  LpgCancelOutline,
  AlertErrorDefault,
  ActionCheckmarkCircleDefault,
  AlertInformationDefault,
  AlertWarningDefault,
} from "@/icons";

import { A11yText } from "../A11yText";
import { Icon } from "../Icon";

import styles from "./Notification.scss";

import {
  NotificationHeadline,
  NotificationHeadlineProps,
} from "./NotificationHeadline";
import { NotificationLink } from "./NotificationLink";
import { NotificationCopyText } from "./NotificationCopyText";

declare const validNotificationMapValue: unique symbol;
export type NotificationVariantValue = {
  icon: JSX.Element;
  styles: string;
} & typeof validNotificationMapValue;

const notificationVariants = {
  information: {
    icon: (
      <Icon
        icon={AlertInformationDefault}
        color={Icon.Color.white}
        size={Icon.Size.parent}
      />
    ),
    styles: styles.information,
  } as NotificationVariantValue,
  confirmation: {
    icon: (
      <Icon
        icon={ActionCheckmarkCircleDefault}
        color={Icon.Color.white}
        size={Icon.Size.parent}
      />
    ),
    styles: styles.confirmation,
  } as NotificationVariantValue,
  warning: {
    icon: (
      <Icon
        icon={AlertWarningDefault}
        color={Icon.Color.black}
        size={Icon.Size.parent}
      />
    ),
    styles: styles.warning,
  } as NotificationVariantValue,
  alert: {
    icon: (
      <Icon
        icon={AlertErrorDefault}
        color={Icon.Color.white}
        size={Icon.Size.parent}
      />
    ),
    styles: styles.alert,
  } as NotificationVariantValue,
} as const;

export type NotificationVariantMap = typeof notificationVariants;
export type NotificationVariantKey = keyof NotificationVariantMap;

type NotificationBaseProps = {
  variant?: NotificationVariantValue;
  customClass?: string;
  wrapperCustomClass?: string;
  closeButtonProps?: JSX.IntrinsicElements["button"];
  closeText?: string;
  hide?: boolean;
  closable?: boolean;
  onClose?: () => void;
  /**
   * @deprecated use <Notification.Headline> in children instead.
   */
  headline?: string;
  headlineProps?: NotificationHeadlineProps;
  noMargins?: boolean;
  noSideMargins?: boolean;
  noTopMargin?: boolean;
  noBottomMargin?: boolean;
};

const defaultProps = {
  variant: notificationVariants.information,
  customClass: undefined,
  wrapperCustomClass: undefined,
  closeText: "Schließen",
  hide: undefined,
  closable: true,
  onClose: undefined,
  headline: undefined,
  headlineProps: {},
  closeButtonProps: {},
  noMargins: false,
  noSideMargins: false,
  noTopMargin: false,
  noBottomMargin: false,
} as const;

export type NotificationProps = NotificationBaseProps &
  Omit<React.ComponentPropsWithoutRef<"div">, keyof NotificationBaseProps>;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
const NotificationWithRef = forwardRef<HTMLDivElement, NotificationProps>(
  (props, forwardedRef) => {
    const {
      variant = defaultProps.variant,
      customClass,
      wrapperCustomClass,
      headline,
      children,
      closeText,
      hide,
      closable,
      onClose,
      headlineProps,
      closeButtonProps,
      noMargins,
      noSideMargins,
      noTopMargin,
      noBottomMargin,
      ...restProps
    } = props;

    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
      if (hide !== undefined) {
        if (hide) {
          setIsOpen(false);
        } else {
          setIsOpen(true);
        }
      }
    }, [hide]);

    if (!isOpen) {
      return <></>;
    }

    const classNames = cx(styles.notification, customClass, {
      [styles.noMargins]: noMargins,
      [styles.noSideMargins]: noSideMargins,
      [styles.noTopMargin]: noTopMargin,
      [styles.noBottomMargin]: noBottomMargin,
    });

    return (
      <div className={cx(styles.wrapper, wrapperCustomClass)}>
        <div ref={forwardedRef} className={classNames} {...restProps}>
          <div
            className={`${styles["icon-container"]} ${variant.styles}`}
            data-testid="icon"
          >
            <span className={styles["icon-wrapper"]}>{variant.icon}</span>
          </div>
          <div className={`${styles.content}`}>
            {headline && (
              <NotificationHeadline {...headlineProps}>
                {headline}
              </NotificationHeadline>
            )}
            {children}
          </div>
          {hide === undefined && closable === true && (
            <button
              className={styles.close}
              type="button"
              onClick={() => {
                setIsOpen(false);
                onClose?.();
              }}
              {...closeButtonProps}
            >
              <Icon icon={LpgCancelOutline} size={Icon.Size.medium} />
              <A11yText>{closeText}</A11yText>
            </button>
          )}
        </div>
      </div>
    );
  }
);

NotificationWithRef.defaultProps = defaultProps;

NotificationWithRef.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  variant: PropTypes.oneOf(Object.values(notificationVariants)),
  customClass: PropTypes.string,
  wrapperCustomClass: PropTypes.string,
  closeButtonProps: PropTypes.shape({}),
  closeText: PropTypes.string,
  hide: PropTypes.bool,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
  /**
   * @deprecated use <Notification.Headline> in children instead.
   */
  headline: PropTypes.string,
  headlineProps: PropTypes.shape({}),
  noMargins: PropTypes.bool,
  noSideMargins: PropTypes.bool,
  noTopMargin: PropTypes.bool,
  noBottomMargin: PropTypes.bool,
};

NotificationWithRef.displayName = "Notification";

export const Notification = Object.assign(NotificationWithRef, {
  Headline: NotificationHeadline,
  Link: NotificationLink,
  CopyText: NotificationCopyText,
  Variant: notificationVariants,
});

export default Notification;
